import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useAuth } from "@/providers";
import { useDomainServices } from "@/providers/DomainServices";

import { cancelUntilDone } from "./cancelUntilDone";

export interface CancelInvocationsByDatasetParams {
  datasetId: string;
}

/**
 * Cancel all active (queued, scheduled, downloading, processing, uploading) invocations for a dataset.
 */
export function useCancelInvocationsByDataset() {
  const { datasetService } = useDomainServices();
  const { currentOrganization } = useAuth();
  const queryClient = useQueryClient();

  return useMutation<undefined, Error, CancelInvocationsByDatasetParams>({
    mutationFn: async ({ datasetId }) => {
      if (currentOrganization === null) {
        return Promise.reject(new Error("Requires login"));
      }

      await cancelUntilDone(() =>
        datasetService.cancelAssociatedInvocations(
          datasetId,
          currentOrganization.org_id,
        ),
      );
    },
    onSuccess: (_, { datasetId }) => {
      // Invalidate active invocations count query (used, e.g, by the 'Cancel All' button) to refresh the UI
      queryClient
        .invalidateQueries({
          queryKey: ["activeInvocationCount", datasetId],
        })
        .catch(() => {
          /** swallow */
        });
    },
  });
}
